import { collection, doc, onSnapshot, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { subscribeByComitentes } from './comitentesQueryService';

/*
 * Acreditaciones y débitos esperados del día por instrumentos en cartera
 * (cupones, amortizaciones, vencimiento de letras y T-Bills, cheques/pagarés MAV). Los calcula la Cloud Function
 * `syncAcreditacionesEsperadas` a partir del SGC; acá solo se leen.
 *
 * Son informativos: no entran en el cálculo de saldos. Lo único que escribe el
 * frontend es la marca del operador (`acreditacionesControl`), en una colección
 * aparte para que las re-corridas del cálculo no la pisen.
 */
const COLLECTION = 'acreditacionesEsperadas';
const META_COLLECTION = 'acreditacionesEsperadasMeta';
const CONTROL_COLLECTION = 'acreditacionesControl';

const RECALCULO_FUNCTION_URL =
  'https://us-central1-planilla-operaciones-axon.cloudfunctions.net/syncAcreditacionesEsperadasPublic';

export const ESTADO_ACREDITADA = 'acreditada';
export const ESTADO_NO_LLEGO = 'no_llego';
export const ESTADO_PENDIENTE = 'pendiente';

const toDocs = (snapshot) => snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

/**
 * Suscribe a los documentos del día (uno por comitente con eventos). En modo
 * productor la consulta tiene que ir acotada por `nroComitente` (reglas), así que
 * se filtra la fecha en memoria.
 */
export const subscribeAcreditacionesDelDia = (fecha, comitentes, onData, onError) => {
  if (Array.isArray(comitentes)) {
    return subscribeByComitentes(
      COLLECTION,
      comitentes,
      (docs) => onData(docs.filter(d => d.fecha === fecha)),
      onError,
    );
  }
  return onSnapshot(
    query(collection(db, COLLECTION), where('fecha', '==', fecha)),
    (snapshot) => onData(toDocs(snapshot)),
    (error) => {
      console.error('acreditacionesEsperadasService.subscribeAcreditacionesDelDia failed:', { fecha, error });
      if (onError) onError(error);
    },
  );
};

/** Suscribe al meta del día: fecha de cartera del SGC, hora de cálculo y error. */
export const subscribeMetaDelDia = (fecha, onData, onError) => onSnapshot(
  doc(db, META_COLLECTION, fecha),
  (snap) => onData(snap.exists() ? { id: snap.id, ...snap.data() } : null),
  (error) => {
    console.error('acreditacionesEsperadasService.subscribeMetaDelDia failed:', { fecha, error });
    if (onError) onError(error);
  },
);

/** Suscribe a las marcas del operador del día. */
export const subscribeControlesDelDia = (fecha, comitentes, onData, onError) => {
  if (Array.isArray(comitentes)) {
    return subscribeByComitentes(
      CONTROL_COLLECTION,
      comitentes,
      (docs) => onData(docs.filter(d => d.fecha === fecha)),
      onError,
    );
  }
  return onSnapshot(
    query(collection(db, CONTROL_COLLECTION), where('fecha', '==', fecha)),
    (snapshot) => onData(toDocs(snapshot)),
    (error) => {
      console.error('acreditacionesEsperadasService.subscribeControlesDelDia failed:', { fecha, error });
      if (onError) onError(error);
    },
  );
};

/**
 * Marca un evento como acreditado, no llegado o lo vuelve a pendiente. Firma con
 * el email del usuario y la hora del servidor (lo exigen las reglas).
 *
 * `montoReal`: pesos que efectivamente llegaron por un dollar linked, con el signo
 * del evento. Solo se guarda al marcarlo Acreditada; cualquier otra marca lo borra
 * (el doc se reescribe entero).
 */
export const marcarAcreditacion = async (user, evento, nroComitente, estado, montoReal = null) => {
  const conMontoReal = estado === ESTADO_ACREDITADA && typeof montoReal === 'number' && Number.isFinite(montoReal);
  try {
    await setDoc(doc(db, CONTROL_COLLECTION, evento.eventId), {
      eventId: evento.eventId,
      fecha: evento.fecha,
      nroComitente: String(nroComitente),
      estado,
      ...(conMontoReal ? { montoReal } : {}),
      marcadoPor: String(user.email || '').trim().toLowerCase(),
      marcadoAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('acreditacionesEsperadasService.marcarAcreditacion failed:', { eventId: evento.eventId, estado, error });
    throw error;
  }
};

/** Pide al backend recalcular las acreditaciones esperadas de hoy contra el SGC. */
export const solicitarRecalculoAcreditaciones = async (user) => {
  try {
    const idToken = await user.getIdToken();
    const response = await fetch(RECALCULO_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`HTTP ${response.status}: ${text}`);
    }
    return response.json();
  } catch (error) {
    console.error('acreditacionesEsperadasService.solicitarRecalculoAcreditaciones failed:', error);
    throw error;
  }
};
